import type { ContractTermsBase } from "./contract-orders.js";
import type { ContractDifficulty, SurveySiteId } from "./progression.js";
import { NPCS, ORDINARY_NPC_ARCHETYPES } from "./npcs.js";
import type { NpcArchetype } from "./contracts.js";
import {
  FACTION_LOCATIONS,
  FACTION_SECTOR_IDS,
  WORLD_NPC_SPAWNS,
  type FactionSectorId,
  type FactionLocationDefinition,
} from "./world-layout.js";

export type TerritoryContractTemplateId =
  `${FactionSectorId}-${"hunt" | "salvage" | "energy"}`;
export const TERRITORY_CONTRACT_TEMPLATE_IDS = FACTION_SECTOR_IDS.flatMap(
  (id): TerritoryContractTemplateId[] => [
    `${id}-hunt`,
    `${id}-salvage`,
    `${id}-energy`,
  ],
);
export interface TerritoryContractTemplate {
  id: TerritoryContractTemplateId;
  family: "hunt" | "salvage" | "signal";
  location: FactionLocationDefinition;
  surveyCount: 0 | 2 | 3;
}
export const TERRITORY_CONTRACT_TEMPLATES: readonly TerritoryContractTemplate[] =
  FACTION_LOCATIONS.flatMap((location, index): TerritoryContractTemplate[] => [
    { id: `${location.id}-hunt`, family: "hunt", location, surveyCount: 0 },
    {
      id: `${location.id}-salvage`,
      family: "salvage",
      location,
      surveyCount: index % 3 === 0 ? 0 : index % 3 === 1 ? 2 : 3,
    },
    { id: `${location.id}-energy`, family: "signal", location, surveyCount: 0 },
  ]);
// Released home-hunt terms remain decodable; fresh boards never offer these hunts.
const RELEASED_HOME_POPULATIONS: Readonly<Record<string, readonly NpcArchetype[]>> = {
  "concord-harmony": ["disruptor", "minelayer", "sniper", "raider"],
  "helion-rift": ["elite", "raider", "raider", "support"],
  "nomads-last-chance": ["elite", "raider", "raider", "disruptor"],
};

export function territoryContractTerms(
  template: TerritoryContractTemplate,
  difficulty: ContractDifficulty,
  base: ContractTermsBase,
): ContractTermsBase {
  const { location, family } = template;
  const advanced = difficulty === "advanced";
  const terms: ContractTermsBase = {
    ...base,
    sectorId: location.id,
    requiredArchetypes: [],
    requiredSurveySites: [],
    requireElite: false,
    requireRescue: false,
  };
  if (family === "hunt") {
    const population = RELEASED_HOME_POPULATIONS[location.id] ?? WORLD_NPC_SPAWNS.filter(
      (npc) => npc.sectorId === location.id,
    ).map((npc) => npc.archetype);
    const specialists = [
      ...new Set(population),
    ].filter(
      (id) =>
        id !== "raider" && ORDINARY_NPC_ARCHETYPES.some((ordinary) => ordinary === id),
    );
    const required: NpcArchetype[] = specialists.length
      ? specialists.slice(0, 2)
      : ["raider"];
    terms.requiredArchetypes = required;
    terms.requireElite =
      advanced && population.includes("elite");
    terms.goal = advanced ? 4 : required.length;
    terms.cargoKind = null;
    terms.cargoGoal = 0;
    terms.title = `${terms.requireElite ? "Командир и прикрытие" : "Охота на специалистов"}: ${location.name}`;
    terms.description = `В области «${location.name}» уничтожь: ${required.map((id) => NPCS[id].name).join(", ")}. Требуется ${terms.goal} ${terms.goal === 1 ? "боевой зачёт" : "боевых зачёта"}${terms.requireElite ? " и подтверждённое уничтожение командира" : ""}. Другие области не подходят.`;
    terms.objective = {
      name: "Уничтожь указанных специалистов",
      ...location.huntPoint,
      kind: "combat",
    };
    terms.credits = advanced ? (terms.requireElite ? "300" : "250") : "150";
    terms.components = advanced ? (terms.requireElite ? 7 : 6) : 3;
  } else {
    terms.goal = terms.cargoGoal = advanced
      ? 2
      : template.surveyCount > 0
        ? 1
        : 2;
    terms.cargoKind = family === "salvage" ? "salvage" : "energy";
    terms.cargoOriginSectorId = location.id;
    const surveyCount =
      family === "salvage" ? (advanced ? 3 : template.surveyCount) : 0;
    const sites: SurveySiteId[] = ([1, 2, 3] as const)
      .slice(0, surveyCount)
      .map((ordinal): SurveySiteId => `${location.id}-scan-${ordinal}`);
    terms.requiredSurveySites = sites;
    terms.title = `${family === "signal" ? "Энергия" : sites.length ? "Разведка и оборудование" : "Сбор оборудования"}: ${location.name}`;
    terms.description = `Собери и сохрани ${terms.cargoGoal} ${terms.cargoGoal === 1 ? "контейнер" : "контейнера"} ${family === "signal" ? "энергии" : "оборудования"} из области «${location.name}»${sites.length ? ` и физически обследуй ${sites.length} скан-маяка` : ""}. Груз из других областей не подходит. Сдай результат на станции.`;
    terms.objective = {
      name: sites.length
        ? "Обследуй скан-маяки и собери оборудование"
        : family === "signal"
          ? "Собери местную энергию"
          : "Собери местное оборудование",
      ...location.collectPoint,
      kind: sites.length ? "survey" : "collect",
    };
    terms.credits = advanced ? "190" : sites.length ? "150" : "100";
    terms.components = advanced ? 5 : 3;
  }
  return terms;
}
